/* eslint-disable react/require-default-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaChevronRight } from 'react-icons/fa';

import {
   LegendItem,
   LegendSwatch,
   ProgressLegend,
   ProgressSegment,
   ProgressTrack,
} from '../../../../styles/ui/ProgressBar';
import { buildItemProgress } from '../utils/progress';
import { productName } from '../utils/format';

// =============================================================================
// Seção "Itens do pedido" do detalhe — o problema visual nº 1 do handoff.
//
// As cinco quantidades por item (pedido, expedido, conferido, tratado,
// a expedir) deixam de ser cinco colunas numéricas: cada linha vira
// chevron · produto · barra empilhada + fração legível, com chips de saldo e
// divergência. Expandir a linha revela os cinco cartões rotulados — os números
// crus continuam todos disponíveis, mas só para quem os pede.
//
// A derivação mora em utils/progress.js (com teste); aqui é só apresentação.
// =============================================================================

const SummaryWrap = styled.div`
   display: grid;
   gap: 7px;
   margin-bottom: 16px;
`;

const SummaryLine = styled.div`
   display: flex;
   align-items: baseline;
   gap: 8px;
   flex-wrap: wrap;

   .pct {
      font-size: 15px;
      font-weight: 800;
      color: #1b5e20;
   }
   .frac {
      font-size: 12px;
      color: ${({ theme }) => theme.colors.textMuted};
   }
`;

const Rows = styled.div`
   display: grid;
`;

const Row = styled.div`
   display: grid;
   grid-template-columns: 20px minmax(160px, 1.6fr) minmax(120px, 1fr);
   gap: 10px 14px;
   align-items: center;
   padding: 13px 16px;
   border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

   &:first-child {
      border-top: none;
   }
   &:hover {
      background: #f7fafd;
   }
`;

const ChevronBtn = styled.button`
   appearance: none;
   border: 0;
   background: transparent;
   padding: 0;
   width: 20px;
   height: 20px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   color: ${({ theme }) => theme.colors.textMuted};
   transition: transform 0.15s ease;
   transform: ${({ $open }) => ($open ? 'rotate(90deg)' : 'none')};

   /* G.4.1: sem hover próprio com background, o default do legado pinta azul. */
   &:hover:not(:disabled) {
      background: transparent;
      color: ${({ theme }) => theme.colors.textPrimary};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
      border-radius: 5px;
   }
`;

const ProductCell = styled.button`
   appearance: none;
   border: 0;
   background: transparent;
   padding: 0;
   min-width: 0;
   text-align: left;
   cursor: pointer;
   font-family: inherit;

   .name {
      display: block;
      font-size: 13.5px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.textPrimary};
      overflow-wrap: anywhere;
   }
   .sku {
      display: block;
      margin-top: 1px;
      font-size: 11px;
      color: ${({ theme }) => theme.colors.textMuted};
   }

   &:hover:not(:disabled) {
      background: transparent;
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
      border-radius: 6px;
   }
`;

const BarCell = styled.div`
   min-width: 0;
   display: grid;
   gap: 5px;

   .frac {
      font-size: 11px;
      color: ${({ theme }) => theme.colors.textMuted};
      white-space: nowrap;
   }
`;

// A faixa de chips ocupa a linha inteira abaixo do par produto/barra e alinha
// à direita — é isso que faz a linha reflowar em vez de esmagar o nome.
const ChipsRow = styled.div`
   grid-column: 2 / -1;
   display: flex;
   gap: 6px;
   flex-wrap: wrap;
   justify-content: flex-end;
`;

const CHIP_TONES = {
   blue: { bg: 'rgba(64,152,239,0.14)', text: '#0b3a63' },
   green: { bg: 'rgba(46,125,50,0.14)', text: '#1b5e20' },
   red: { bg: 'rgba(220,38,38,0.12)', text: '#a01b1b' },
   gray: { bg: 'rgba(0,0,0,0.07)', text: '#555' },
};

const Chip = styled.span`
   display: inline-flex;
   align-items: center;
   padding: 3px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   white-space: nowrap;
   background: ${({ $tone }) => (CHIP_TONES[$tone] ?? CHIP_TONES.gray).bg};
   color: ${({ $tone }) => (CHIP_TONES[$tone] ?? CHIP_TONES.gray).text};
`;

const StatCards = styled.div`
   grid-column: 1 / -1;
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
   gap: 8px;
   padding-top: 2px;
`;

const StatCard = styled.div`
   border: 1px solid ${({ theme }) => theme.colors.border};
   border-radius: 10px;
   padding: 9px 12px;
   background: #fff;

   .lbl {
      font-size: 10.5px;
      font-weight: 800;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.textMuted};
   }
   .val {
      margin-top: 2px;
      font-size: 19px;
      font-weight: 800;
      color: ${({ theme }) => theme.colors.textPrimary};
      font-variant-numeric: tabular-nums;
   }
   .cap {
      margin-top: 1px;
      font-size: 10.5px;
      color: ${({ theme }) => theme.colors.textMuted};
   }
`;

function SegmentsBar({ segments }) {
   return (
      <ProgressTrack>
         {segments.received > 0 && (
            <ProgressSegment $tone="received" $pct={segments.received} />
         )}
         {segments.missing > 0 && (
            <ProgressSegment $tone="missing" $pct={segments.missing} />
         )}
         {segments.transit > 0 && (
            <ProgressSegment $tone="transit" $pct={segments.transit} />
         )}
      </ProgressTrack>
   );
}

SegmentsBar.propTypes = {
   segments: PropTypes.shape({
      received: PropTypes.number,
      missing: PropTypes.number,
      transit: PropTypes.number,
   }).isRequired,
};

function Legend({ hasMissing }) {
   return (
      <ProgressLegend>
         <LegendItem>
            <LegendSwatch $tone="received" /> Conferido
         </LegendItem>
         {/* "Falta" só entra na legenda quando há perda. */}
         {hasMissing && (
            <LegendItem>
               <LegendSwatch $tone="missing" /> Falta
            </LegendItem>
         )}
         <LegendItem>
            <LegendSwatch $tone="transit" /> Em trânsito
         </LegendItem>
         <LegendItem>
            <LegendSwatch $tone="track" /> A expedir
         </LegendItem>
      </ProgressLegend>
   );
}

Legend.propTypes = { hasMissing: PropTypes.bool.isRequired };

// Chip único de saldo do item — a fração que importa para decidir a próxima
// ação (é o mesmo dado que habilita "Expedir remessa").
function saldoChip(progress) {
   if (progress.remaining > 0)
      return { tone: 'blue', label: `A expedir ${progress.remaining}` };
   if (progress.inTransit > 0)
      return { tone: 'blue', label: `Em trânsito ${progress.inTransit}` };
   if (progress.missing > 0) return { tone: 'gray', label: 'Contagem fechada' };
   return { tone: 'green', label: 'Completo' };
}

function ItemRow({ item, shipments }) {
   const [open, setOpen] = useState(false);
   const progress = useMemo(
      () => buildItemProgress(item, shipments),
      [item, shipments]
   );

   const saldo = saldoChip(progress);
   const toggle = () => setOpen((prev) => !prev);
   const rowId = `transfer-item-${item.prod_id}`;

   return (
      <Row>
         <ChevronBtn
            type="button"
            $open={open}
            onClick={toggle}
            aria-expanded={open}
            aria-controls={rowId}
            aria-label={`Detalhar quantidades de ${productName(item)}`}
         >
            <FaChevronRight size={11} />
         </ChevronBtn>

         <ProductCell type="button" onClick={toggle} tabIndex={-1}>
            <span className="name">{productName(item)}</span>
            <span className="sku">SKU {item.prod_sku ?? '—'}</span>
         </ProductCell>

         <BarCell>
            <SegmentsBar segments={progress.segments} />
            <span className="frac">
               {progress.received} de {progress.requested} conferidos
            </span>
         </BarCell>

         <ChipsRow>
            <Chip $tone={saldo.tone}>{saldo.label}</Chip>
            {/* Divergência só existe no detalhe — na listagem o backend nem
                carrega as remessas. Pendente (vermelho) vira "Tratada" (verde)
                quando o gestor fecha. */}
            {progress.missingPending > 0 && (
               <Chip $tone="red">Divergência: −{progress.missingPending}</Chip>
            )}
            {progress.missingPending === 0 && progress.missingSettled > 0 && (
               <Chip $tone="green">Tratada: −{progress.missingSettled}</Chip>
            )}
         </ChipsRow>

         {open && (
            <StatCards id={rowId}>
               <StatCard>
                  <div className="lbl">Pedido</div>
                  <div className="val">{progress.requested}</div>
                  <div className="cap">declarado na origem</div>
               </StatCard>
               <StatCard>
                  <div className="lbl">Expedido</div>
                  <div className="val">{progress.shipped}</div>
                  <div className="cap">saiu da origem</div>
               </StatCard>
               <StatCard>
                  <div className="lbl">Conferido</div>
                  <div className="val">{progress.received}</div>
                  <div className="cap">entrou no destino</div>
               </StatCard>
               <StatCard>
                  <div className="lbl">Tratado</div>
                  <div className="val">{progress.settled}</div>
                  <div className="cap">divergência fechada</div>
               </StatCard>
               <StatCard>
                  <div className="lbl">A expedir</div>
                  <div className="val">{progress.remaining}</div>
                  <div className="cap">
                     {progress.remaining > 0
                        ? 'reservado na origem'
                        : 'nada pendente'}
                  </div>
               </StatCard>
            </StatCards>
         )}
      </Row>
   );
}

ItemRow.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   item: PropTypes.object.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   shipments: PropTypes.array.isRequired,
};

// Faixa de agregado no topo da seção: % conferido + barra + legenda.
export function OrderProgressSummary({ progress }) {
   return (
      <SummaryWrap>
         <SummaryLine>
            <span className="pct">{progress.pctReceived}%</span>
            <span className="frac">
               conferido no destino · {progress.received} de{' '}
               {progress.requested} un
            </span>
         </SummaryLine>
         <SegmentsBar segments={progress.segments} />
         <Legend hasMissing={progress.missing > 0} />
      </SummaryWrap>
   );
}

OrderProgressSummary.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   progress: PropTypes.object.isRequired,
};

export default function OrderItemsProgress({ items, shipments }) {
   return (
      <Rows>
         {items.map((item) => (
            <ItemRow key={item.prod_id} item={item} shipments={shipments} />
         ))}
      </Rows>
   );
}

OrderItemsProgress.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   items: PropTypes.array.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   shipments: PropTypes.array.isRequired,
};
